import { useEffect, useRef, useState } from 'react'
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../api/client'
import type {
  CardMark,
  TextAnalysis,
  TextBudgetCheck,
  TextBudgetState,
  TextCarded,
  TextDetail,
  GapLabelOptions,
  TextPreview,
  TextPreviewGap,
  TextUpdatePayload,
  TextUpdateResult,
  TextFlowPayload,
  TextFlowSaveResult,
  TextGapMatchResult,
  TextLimits,
  TextList,
  TextWaitingAddResult,
} from '../types'
import { useTextFlowStore } from '../stores/textFlowStore'
import { apiErrorCode } from '../utils/textFlowErrors'

/** The limits the server enforces. Read, never copied: the flow shows them in its copy. */
export function useTextLimits() {
  return useQuery<TextLimits>({
    queryKey: ['text-limits'],
    queryFn: () => api.get<TextLimits>('/api/v1/texts/limits').then((r) => r.data),
    staleTime: Infinity,
  })
}

/** Step 2: the body's words, groups and pre-selection. Stores nothing. */
export function useAnalyseText() {
  return useMutation<TextAnalysis, Error, { text: string }>({
    mutationFn: ({ text }) =>
      api.post<TextAnalysis>('/api/v1/texts/analyse', { text }).then((r) => r.data),
  })
}

/** Step 1, file source: the file's text for the editable area. Stores nothing. */
export function useExtractTextFile() {
  return useMutation<{ filename: string; text: string }, Error, File>({
    mutationFn: (file) => {
      const form = new FormData()
      form.append('file', file)
      return api
        .post<{ filename: string; text: string }>('/api/v1/texts/extract-file', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((r) => r.data)
    },
  })
}

/**
 * Step 2: keeps the store's gap occurrences in step with the selected forms, from
 * the server's matcher (POST /texts/flow/gaps; ADR-115 Decision 4: the client never
 * searches the body). Every change sends the whole set, because overlaps are
 * resolved over all forms at once. A late answer never overwrites a newer one; a
 * failed latest request keeps the marks as tapped and sets `gapMatchFailed`.
 * Returns a retry.
 */
export function useGapMatchSync(body: string, forms: string[], enabled: boolean): () => void {
  const setGapMatch = useTextFlowStore((s) => s.setGapMatch)
  const sent = useRef(0)
  const applied = useRef(0)
  const [attempt, setAttempt] = useState(0)
  const key = JSON.stringify(forms)

  useEffect(() => {
    if (!enabled) return
    const mine = ++sent.current
    const wanted: string[] = JSON.parse(key)
    if (wanted.length === 0) {
      applied.current = mine
      setGapMatch({ forms: [], occurrences: {}, resolved: {} }, false)
      return
    }
    api
      .post<TextGapMatchResult>('/api/v1/texts/flow/gaps', { body, forms: wanted })
      .then(({ data }) => {
        if (mine <= applied.current) return // a newer answer is already shown
        applied.current = mine
        const occurrences: Record<string, [number, number][]> = {}
        const resolved: Record<string, string | null> = {}
        for (const g of data.gaps) {
          occurrences[g.submitted] = g.occurrences
          resolved[g.submitted] = g.form
        }
        const stillFailed = mine === sent.current ? false : useTextFlowStore.getState().gapMatchFailed
        setGapMatch({ forms: wanted, occurrences, resolved }, stillFailed)
      })
      .catch(() => {
        if (mine === sent.current) setGapMatch(useTextFlowStore.getState().gapMatch, true)
      })
  }, [body, key, enabled, attempt, setGapMatch])

  return () => setAttempt((n) => n + 1)
}

/** Step 3: what the save would do with the card marks now. Writes and charges nothing. */
export function useTextBudgetCheck() {
  return useMutation<TextBudgetCheck, Error, TextFlowPayload>({
    mutationFn: (payload) =>
      api.post<TextBudgetCheck>('/api/v1/texts/flow/budget', payload).then((r) => r.data),
  })
}

export function useSaveTextFlow() {
  const qc = useQueryClient()
  return useMutation<TextFlowSaveResult, Error, TextFlowPayload>({
    mutationFn: (payload) =>
      api.post<TextFlowSaveResult>('/api/v1/texts/flow/save', payload).then((r) => r.data),
    onSuccess: () => {
      // A new deck or new cards: every deck list and the queue must show them.
      // A new text, too: the Texts list and the deck rows' text counts.
      qc.invalidateQueries({ queryKey: ['texts'] })
      qc.invalidateQueries({ queryKey: ['decks'] })
      qc.invalidateQueries({ queryKey: ['deck'] })
      qc.invalidateQueries({ queryKey: ['queue-composition'] })
    },
  })
}

/**
 * The learner's texts, most recent activity first (Library → Mine → «Тексты»).
 * Shared with the neighbour prefetch; `abortable` is for the prefetch only.
 */
export const textsListQuery = (abortable = false) => queryOptions({
  queryKey: ['texts'],
  queryFn: async (ctx) => {
    const { data } = await api.get<TextList>('/api/v1/texts', abortable ? { signal: ctx.signal } : undefined)
    return data
  },
  // As the deck list: arriving after the neighbour prefetch does not fetch again.
  staleTime: 30_000,
})

export function useTextList() {
  return useQuery(textsListQuery())
}

/**
 * The texts linked to one deck, most recent activity first (the deck screen's
 * «Тексты»). `wordCounts` also asks for each text's `words_in_deck`, which the
 * server works out by lemma at read time: the phone's rows show it, the desktop's
 * do not, so only the phone pays for it. Under ['texts'], so every change to a text
 * refreshes it.
 */
export const deckTextsQuery = (deckId: string, wordCounts: boolean) => queryOptions({
  queryKey: ['texts', 'deck', deckId, wordCounts],
  queryFn: async () => {
    const { data } = await api.get<TextList>('/api/v1/texts', {
      params: { deck_id: deckId, ...(wordCounts ? { word_counts: true } : {}) },
    })
    return data
  },
  staleTime: 30_000,
})

export function useDeckTexts(deckId: string, wordCounts: boolean) {
  return useQuery(deckTextsQuery(deckId, wordCounts))
}

/** Deletes the text, its gaps, links and practice. No card is touched (ADR-115 Decision 2). */
export function useDeleteText() {
  const qc = useQueryClient()
  return useMutation<string, Error, string>({
    mutationFn: async (id) => {
      await api.delete(`/api/v1/texts/${id}`)
      return id
    },
    onSuccess: (id) => {
      qc.invalidateQueries({ queryKey: ['texts'] })
      // Deck rows count their linked texts, and so does the deck screen's switch.
      qc.invalidateQueries({ queryKey: ['decks'] })
      qc.invalidateQueries({ queryKey: ['deck'] })
      qc.removeQueries({ queryKey: ['text', id] })
    },
  })
}

/**
 * The text-import word budget as it stands now: words left today and when it
 * refreshes, both the server's. The text screen's waiting-words card reads it for
 * «Добавить {m} из {k}»; the add action itself stays the only thing that spends it.
 */
export function useTextBudget(enabled = true) {
  return useQuery<TextBudgetState>({
    queryKey: ['text-budget'],
    queryFn: () => api.get<TextBudgetState>('/api/v1/texts/budget').then((r) => r.data),
    enabled,
    // A figure that other tabs, and the generation run, change: read it again each time.
    staleTime: 0,
  })
}

/**
 * The one-tap add of a text's waiting words (ADR-115 Amendment 2): the server adds as
 * many as today's budget allows, earliest in text order first, under the text's row
 * lock, so a repeated or concurrent tap makes no card twice. Refused before the
 * budget refreshes (409 TEXT_WAITING_NOT_YET, with the refresh time).
 */
export function useAddWaitingWords(textId: string) {
  const qc = useQueryClient()
  return useMutation<TextWaitingAddResult, Error, void>({
    mutationFn: () =>
      api.post<TextWaitingAddResult>(`/api/v1/texts/${textId}/waiting/add`).then((r) => r.data),
    onSettled: () => {
      // Cards, the waiting queue and the budget all changed (or, after a refusal, are stale).
      qc.invalidateQueries({ queryKey: ['text', textId] })
      qc.invalidateQueries({ queryKey: ['texts'] })
      qc.invalidateQueries({ queryKey: ['decks'] })
      qc.invalidateQueries({ queryKey: ['deck'] })
      qc.invalidateQueries({ queryKey: ['text-budget'] })
      qc.invalidateQueries({ queryKey: ['queue-composition'] })
    },
  })
}

/** The text screen's read: the text, its gaps, its decks and its waiting words. */
export function useText(id: string | undefined) {
  return useQuery<TextDetail>({
    queryKey: ['text', id],
    queryFn: () => api.get<TextDetail>(`/api/v1/texts/${id}`).then((r) => r.data),
    enabled: !!id,
  })
}

// ── Texts: edit mode (ADR-115 Decision 5, handoff §15) ───────────────────────

/** A preview as the edit screen holds it: the draft it was made for, and the server's answer. */
export interface AppliedPreview {
  body: string
  forms: string[]
  gaps: TextPreviewGap[]
}

const PREVIEW_DEBOUNCE_MS = 250

/**
 * Live effect of the draft body and gap set on the gaps (POST /texts/{id}/preview;
 * nothing is written). The client never searches the body: every occurrence it draws
 * is the server's (ADR-115 Decision 4). Requests are debounced, and a late answer
 * never overwrites a newer one: only the answer to the latest request, or to one
 * later than what is shown, is applied.
 *
 * `current` is the latest applied preview and `previous` the one it replaced (the
 * change strip compares them). A refused or failed latest request keeps `current` and
 * sets `error` (the server's code, or 'NETWORK'); `retry` asks again.
 */
export function useEditPreview(
  textId: string,
  body: string,
  forms: string[],
  seed: AppliedPreview,
  enabled: boolean,
) {
  const [shown, setShown] = useState<{ current: AppliedPreview; previous: AppliedPreview | null }>({
    current: seed, previous: null,
  })
  const [error, setError] = useState<string | null>(null)
  const [attempt, setAttempt] = useState(0)
  const sent = useRef(0)
  const applied = useRef(0)
  const key = JSON.stringify(forms)
  const seedBody = seed.body

  useEffect(() => {
    if (!enabled) return
    const wanted: string[] = JSON.parse(key)
    if (body === shown.current.body && key === JSON.stringify(shown.current.forms) && attempt === 0) {
      sent.current += 1
      applied.current = sent.current
      setError(null)
      return
    }
    const mine = ++sent.current
    const timer = window.setTimeout(() => {
      api
        .post<TextPreview>(`/api/v1/texts/${textId}/preview`, { body, forms: wanted } satisfies TextUpdatePayload)
        .then(({ data }) => {
          if (mine <= applied.current) return // a newer answer is already shown
          applied.current = mine
          setShown((s) => ({ current: { body, forms: wanted, gaps: data.gaps }, previous: s.current }))
          if (mine === sent.current) setError(null)
        })
        .catch((err) => {
          if (mine === sent.current) setError(apiErrorCode(err) ?? 'NETWORK')
        })
    }, PREVIEW_DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
    // `shown` is deliberately not a dependency: a new answer must not trigger a request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textId, body, key, enabled, attempt, seedBody])

  return { ...shown, error, retry: () => setAttempt((n) => n + 1) }
}

/** The forms a gap can be set to, from the server (GET …/label-options). */
export function useGapLabelOptions(textId: string, gapId: string, enabled: boolean) {
  return useQuery<GapLabelOptions>({
    queryKey: ['text', textId, 'gap', gapId, 'label-options'],
    queryFn: () =>
      api.get<GapLabelOptions>(`/api/v1/texts/${textId}/gaps/${gapId}/label-options`).then((r) => r.data),
    enabled,
    staleTime: 60_000,
  })
}

/**
 * Edit mode's budget check (POST /texts/{id}/budget): for the same PATCH body, which
 * card words would become cards in the receiving deck, wait or be skipped. Writes and
 * charges nothing.
 */
export function useEditBudgetCheck(textId: string) {
  return useMutation<TextBudgetCheck, Error, TextUpdatePayload>({
    mutationFn: (payload) =>
      api.post<TextBudgetCheck>(`/api/v1/texts/${textId}/budget`, payload).then((r) => r.data),
  })
}

/**
 * Edit mode's picker: which words of *body* (or, with *spans*, which of those spans)
 * already have a card in *deckId*, by the rule the save skips them with. The server
 * works it out when asked and stores nothing (ADR-115 Amendment 3 Decision 6).
 */
export function useCardedWords(
  textId: string | undefined,
  deckId: string | null,
  body: string,
  spans: CardMark[] | null,
) {
  return useQuery<TextCarded>({
    queryKey: ['text', textId, 'carded', deckId, body, spans],
    queryFn: () =>
      api.post<TextCarded>(`/api/v1/texts/${textId}/carded`, {
        deck_id: deckId, body, ...(spans ? { spans } : {}),
      }).then((r) => r.data),
    enabled: !!textId && !!deckId && body.trim() !== '' && (spans === null || spans.length > 0),
    staleTime: 30_000,
  })
}

/** Saves an edit in one request: everything or nothing (PATCH /texts/{id}). */
export function useUpdateText(textId: string) {
  const qc = useQueryClient()
  return useMutation<TextUpdateResult, Error, TextUpdatePayload>({
    mutationFn: (payload) =>
      api.patch<TextUpdateResult>(`/api/v1/texts/${textId}`, payload).then((r) => r.data),
    onSuccess: (data) => {
      qc.setQueryData(['text', textId], data)
      qc.invalidateQueries({ queryKey: ['text', textId, 'gap'] })
      qc.invalidateQueries({ queryKey: ['texts'] })
      // A deck's rows count their linked texts.
      qc.invalidateQueries({ queryKey: ['decks'] })
      qc.invalidateQueries({ queryKey: ['deck'] })
      // Card marks may have made a deck and cards (ADR-115 Amendment 3).
      if (data.cards) qc.invalidateQueries({ queryKey: ['queue-composition'] })
    },
  })
}
